import { Injectable } from '@angular/core';
import { deleteField, doc, Firestore, getDoc, updateDoc } from '@firebase/firestore';
import { AuthService } from './auth.service';
import { getPeechFirestore } from './peech-firestore';

// users/{uuid}.pausedItem — the cross-device resume point, mirroring iOS
// FirestoreUserDataSource. playedTime is seconds WITHIN the item's current part.
// partIndex is a web-only additive field: the item doc's listeningPartIndex only ever
// advances (updateListeningProgress is monotonic), so a pause on an EARLIER part must
// carry its own part or the offset restores against the wrong one. iOS ignores unknown
// keys on decode and simply omits the field on its own writes — absent means "fall back
// to listeningPartIndex", which is exactly iOS behavior.
export interface PausedItem {
  id: string;
  playedTime: number;
  partIndex: number | null;
}

// Reads/writes of the pausedItem field on the user doc. Writes are fire-and-forget on
// purpose: the resume point is best-effort (a pagehide write may never flush — iOS
// handleAppTermination accepts the same), and playback must never block on it. None of
// these writes touch updatedAt or any other field.
@Injectable({ providedIn: 'root' })
export class PausedItemService {
  private readonly firestore: Firestore;

  constructor(auth: AuthService) {
    this.firestore = getPeechFirestore(auth.app);
  }

  // One-shot read; a missing doc, absent field, or malformed shape all mean "nothing to
  // restore" — never an error the caller must handle.
  async read(peechUserId: string): Promise<PausedItem | null> {
    try {
      const snapshot = await getDoc(this.userDoc(peechUserId));
      const raw = snapshot.data()?.['pausedItem'];
      const id = (raw as { id?: unknown })?.id;
      const playedTime = (raw as { playedTime?: unknown })?.playedTime;
      const partIndex = (raw as { partIndex?: unknown })?.partIndex;

      if (typeof id !== 'string' || !id || typeof playedTime !== 'number') {
        return null;
      }

      return {
        id,
        playedTime: Math.max(0, playedTime),
        partIndex: typeof partIndex === 'number' && partIndex >= 0 ? partIndex : null,
      };
    } catch {
      return null;
    }
  }

  // iOS writes pausedItem.id lowercased (PausedItem.id.lowercasedString) — match the wire
  // shape exactly so both clients read each other's writes. partIndex rides along
  // atomically (see the interface note); iOS decodes past it.
  save(peechUserId: string, itemId: string, playedTime: number, partIndex: number): void {
    void updateDoc(this.userDoc(peechUserId), {
      pausedItem: { id: itemId.toLowerCase(), playedTime, partIndex },
    }).catch(() => undefined);
  }

  clear(peechUserId: string): void {
    void updateDoc(this.userDoc(peechUserId), { pausedItem: deleteField() }).catch(() => undefined);
  }

  private userDoc(peechUserId: string) {
    return doc(this.firestore, 'users', peechUserId.toLowerCase());
  }
}
